/**
 * RET-546 position ownership proof (client side).
 *
 * When a queue enables ownership verification, the API masks the customer's PII
 * and returns `ownershipAuthRequired: true` on the position until the customer
 * proves ownership by supplying the value of a configured registration field
 * (e.g. their phone number). The app collects that value once via a popup and
 * replays it as the `X-Position-Auth` header on the position read and on
 * cancel/reschedule.
 *
 * The value is the customer's own PII, so we keep it only in `sessionStorage`
 * (cleared when the tab closes), keyed per position — never in localStorage and
 * never in the URL.
 */

export const OWNERSHIP_HEADER = 'X-Position-Auth';

const storageKey = (positionId: string) => `grid-queue-ownership-proof:${positionId}`;

const safeSession = (): Storage | undefined => {
  try {
    return window.sessionStorage;
  } catch {
    // sessionStorage can throw in privacy modes / sandboxed iframes.
    return undefined;
  }
};

export const setOwnershipProof = (positionId: string, value: string): void => {
  safeSession()?.setItem(storageKey(positionId), value);
};

export const getOwnershipProof = (positionId?: string): string | undefined => {
  if (!positionId) return undefined;
  return safeSession()?.getItem(storageKey(positionId)) ?? undefined;
};

export const clearOwnershipProof = (positionId?: string): void => {
  if (!positionId) return;
  safeSession()?.removeItem(storageKey(positionId));
};

/**
 * Header object to spread into an axios request for the given position. Empty
 * when no proof is stored, so requests are byte-for-byte unchanged for queues
 * that have not opted in.
 */
export const ownershipHeader = (positionId?: string): Record<string, string> => {
  const proof = getOwnershipProof(positionId);
  return proof ? { [OWNERSHIP_HEADER]: proof } : {};
};
